import { useEffect, useState } from "react";
import {
  BUTTON_LABEL,
  LABELS,
  toastError,
  toastSuccess,
} from "../../../../shared/constants";
import Navbar from "../../../Navbar/NavbarComponent";
import SimpleButton from "../../../../shared/components/Button/Button";
import ButtonLoader from "../../../../shared/components/ButtonLoader";
import CheckBox from "../../../../shared/components/CheckBox/CheckBox";
import CustomModal from "../../../../shared/components/CustomModal";
import Loader from "../../../../shared/components/Loader";
import {
  useAuditFieldsGetQuery,
  useAuditFieldsUpdateMutation,
} from "../../../../Features/settings/settings-slice";

const getFieldLabel = (key: string): string => {
  const customLabels: Record<string, string> = {
    itemCode: "Item Code",
    qty: "Quantity",
    price: "Price",
    wsp: "WSP",
    size: "Size",
    color: "Color",
    remarks: "Remarks",
    designNo: "Design No",
    fabricCode: "Fabric Code",
    salesPerson: "Sales Person",
    customerName: "Customer Name",
    customerMobile: "Phone",
    address: "Address",
    "measurement.top.sherwani": "Top Measurement - Sherwani",
    "measurement.top.indo": "Top Measurement - Indo",
    "measurement.top.jodhpuri": "Top Measurement - Jodhpuri",
    "measurement.top.coat": "Top Measurement - Coat",
    "measurement.top.alika": "Top Measurement - Alika",
    "measurement.top.kurta": "Top Measurement - Kurta",
    "measurement.top.bandi": "Top Measurement - Bandi",
    "measurement.bottom": "Bottom Measurement",
    "measurement.custom.styleAndFabric": "Customization - Style & Fabric",
    "measurement.custom.cutStyle": "Customization - Cut Style",
    "measurement.custom.fabricChange": "Customization - Fabric Change",
    "measurement.custom.workChange": "Customization - Work Change",
    "measurement.custom.bodyInstruction": "Customization - Body Instruction",
    "measurement.accessories.mojdiShoes": "Accessories - Mojdi Shoes",
    "measurement.accessories.dupatta": "Accessories - Dupatta",
    "measurement.accessories.kamarbandh": "Accessories - Kamarbandh",
  };

  return customLabels[key] || key;
};

const AuditTrailConfig = () => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    data: auditFieldsData,
    isFetching: auditFieldsFetching,
    isSuccess: auditFieldsSuccess,
    refetch: auditFieldsRefetch,
  } = useAuditFieldsGetQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [auditFieldsUpdate, { isLoading: fieldsUpdateLoading }] =
    useAuditFieldsUpdateMutation();

  useEffect(() => {
    if (auditFieldsSuccess && auditFieldsData) {
      setSelectedFields(auditFieldsData.item.fields || []);
    }
  }, [auditFieldsData, auditFieldsSuccess]);

  // The server's `available` array is the single source of truth (TSK-OO-007);
  // nothing is hardcoded client-side.
  const available = auditFieldsData?.item?.available || [];

  const isAllSelected =
    available.length > 0 && selectedFields.length === available.length;

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      setSelectedFields([]);
    } else {
      setSelectedFields([...available]);
    }
  };

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldKey)
        ? prev.filter((key) => key !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  const handleSaveFields = async () => {
    const response = await auditFieldsUpdate({ fields: selectedFields })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg || "Failed to update fields");
      });

    if (response && response.status) {
      toastSuccess(response.message);
      auditFieldsRefetch();
    }
    setShowConfirm(false);
  };

  return (
    <>
      {auditFieldsFetching ? (
        <Loader />
      ) : (
        <div className="full-height">
          <Navbar title={LABELS.AUDIT_TRAIL_CONFIG_TEXT} />
          <main className="main-content">
            <div className="pt-3">
              <div className="container-fluid">
                <div className="card custom-card mb-4">
                  <div className="card-header border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                      <h5 className="mb-0 text-black font-weight-600">
                        {LABELS.AUDIT_TRAIL_CONFIG_TEXT}
                      </h5>
                      <small className="text-muted">
                        Select which order fields are tracked for changes.
                      </small>
                    </div>
                    {available.length > 0 && (
                      <div className="d-flex align-items-center gap-3">
                        <CheckBox
                          id="audit-field-select-all"
                          label="Select All"
                          value={isAllSelected}
                          onChange={handleSelectAllToggle}
                          name="audit-field-select-all"
                        />
                        <span className="badge bg-light text-dark font-weight-500 border ms-2">
                          {selectedFields.length} / {available.length} Selected
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {available.map((fieldKey: string) => (
                        <div
                          key={fieldKey}
                          className="col-md-3 col-sm-6 mb-3"
                        >
                          <CheckBox
                            id={`audit-field-${fieldKey}`}
                            label={getFieldLabel(fieldKey)}
                            value={selectedFields.includes(fieldKey)}
                            onChange={() => handleFieldToggle(fieldKey)}
                            name={`audit-field-${fieldKey}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="row mt-3 align-items-center">
                      <div className="col-12 col-md-6">
                        <small className="text-muted">
                          {LABELS.AUDIT_CONFIG_WARNING_TEXT}
                        </small>
                      </div>
                      <div className="col-12 col-md-6 text-end">
                        <SimpleButton
                          className="btn theme-btn theme-btn-sm"
                          onClick={() => setShowConfirm(true)}
                          text={BUTTON_LABEL.SAVE}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      )}

      <CustomModal
        show={showConfirm}
        close={() => setShowConfirm(false)}
        size="md"
        message={LABELS.AUDIT_CONFIG_WARNING_TEXT}
        children={undefined}
        modalTitle={BUTTON_LABEL.CONFIRM}
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: () => setShowConfirm(false),
            className: "btn theme-btn btn-inverse me-2",
          },
          {
            text: fieldsUpdateLoading ? <ButtonLoader /> : BUTTON_LABEL.CONFIRM,
            action: handleSaveFields,
            className: "btn theme-btn",
            isLoading: fieldsUpdateLoading,
          },
        ]}
        fullscreen={undefined}
        className=""
        centerClass=""
        modalTitleIcon=""
        footerClass="d-flex justify-content-end p-3 border-top"
      />
    </>
  );
};

export default AuditTrailConfig;
